import {clearAuthSession, getAuthHeader} from './auth';

export class HttpError extends Error {
    constructor(statusCode, message) {
        super(message);
        this.statusCode = statusCode;
        this.name = 'HttpError';
    }
}

function buildHeaders(headers) {
    return {
        ...(headers || {}),
        ...getAuthHeader(),
    };
}

function handleAuthFailure(errorCode) {
    clearAuthSession();
    const currentPath = window.location.pathname + window.location.search;
    if (currentPath !== '/login' && !currentPath.startsWith('/login?')) {
        sessionStorage.setItem('redirectAfterLogin', currentPath);
    }
    const qs = errorCode ? `?reason=${encodeURIComponent(errorCode)}` : '';
    const loginTarget = '/login' + qs;
    if (currentPath !== loginTarget) {
        window.location.replace(loginTarget);
    } else {
        window.location.reload();
    }
}

export function makeRequest(url, method, headers, body, timeout = 20000, retries = 5) {
    let retryCount = 0;

    const fetchWithTimeout = (resourceUrl, options) => {
        return Promise.race([
            fetch(resourceUrl, options),
            new Promise((_, reject) =>
                setTimeout(() => reject(new Error('Request timed out')), timeout)
            ),
        ]);
    };

    const attemptRequest = () => {
        const options = {
            method,
            // Reavalia o token em cada tentativa, evitando reenviar um JWT que tenha expirado entre retries.
            headers: buildHeaders(headers),
            body,
        };

        return fetchWithTimeout(url, options)
            .then(response => {
                if (response.status === 401) {
                    return response.text().then(text => {
                        let errorCode = 'AUTH_TOKEN_INVALID';
                        try {
                            const j = JSON.parse(text);
                            if (j.errorCode) errorCode = j.errorCode;
                        } catch (e) { /* ignore */ }
                        handleAuthFailure(errorCode);
                        throw new HttpError(401, text || 'Unauthorized');
                    });
                }
                if (response.status === 403) {
                    return response.text().then(text => {
                        let errorCode = '';
                        try {
                            const j = JSON.parse(text);
                            if (j.errorCode) errorCode = j.errorCode;
                            if (j.message && j.errorCode === 'OUTSIDE_WORK_HOURS') {
                                sessionStorage.setItem('loginMessage', j.message);
                            }
                        } catch (e) { /* ignore */ }
                        // Só encerra a sessão quando o backend indica bloqueio de horário de trabalho.
                        // Outros 403 (ex.: "Acesso negado" a um recurso) não invalidam o JWT.
                        if (errorCode === 'OUTSIDE_WORK_HOURS') {
                            handleAuthFailure(errorCode);
                        }
                        throw new HttpError(403, text || 'Forbidden');
                    });
                }
                if (response.status === 409) {
                    return response.text().then(text => {
                        throw new HttpError(response.status, text || 'Conflict');
                    });
                }
                if (!response.ok) {
                    throw new HttpError(response.status, `Request error: ${response.statusText}`);
                }
                if (method === 'DELETE') {
                    return response;
                }
                return response.json();
            })
            .catch(error => {
                if (error instanceof HttpError && (error.statusCode === 401 || error.statusCode === 403)) {
                    throw error;
                }
                retryCount++;
                if (retryCount < retries) {
                    console.log(`Retrying (${retryCount}/${retries})...`);
                    return attemptRequest();
                } else {
                    console.error('Request error:', error);
                    throw error;
                }
            });
    };

    return attemptRequest();
}
